import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Package, Tag, MessageSquare, Building2, Users, UtensilsCrossed, Percent, BarChart3, FileText, Settings, LogOut, Menu, X, User, MessageCircle, Inbox, Truck, Briefcase, DollarSign, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useOrders } from '../contexts/OrderContext';
import { filterTodayOrders } from '../utils/dateFilters';
import { getNewRequestsCount } from '../utils/demoRequests';
import { isSupabaseConfigured } from '../lib/supabase';
interface AdminSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
export function AdminSidebar({
  isOpen,
  setIsOpen
}: AdminSidebarProps) {
  const {
    user,
    logout
  } = useAuth();
  const {
    unreadTotal
  } = useChat();
  const {
    orders
  } = useOrders();
  const navigate = useNavigate();
  const location = useLocation();
  const isDeliveryOnly = (user as any)?.isDeliveryOnly === true;
  // Track new orders that arrived since last time user viewed the orders page
  const [newOrderCount, setNewOrderCount] = useState(0);
  // Listen for new order events and increment counter
  useEffect(() => {
    const handleNewOrder = () => {
      // Only increment if user is NOT currently on the orders page
      if (!window.location.pathname.includes('/admin/orders')) {
        setNewOrderCount(prev => prev + 1);
      }
    };
    window.addEventListener('cricket-new-order', handleNewOrder);
    return () => window.removeEventListener('cricket-new-order', handleNewOrder);
  }, []);
  // Reset counter when user navigates to orders page
  useEffect(() => {
    if (location.pathname === '/admin/orders') {
      setNewOrderCount(0);
    }
  }, [location.pathname]);
  // Count today's pending orders using the same filter as the dashboard
  const todayOrders = filterTodayOrders(orders);
  const pendingOrdersCount = todayOrders.filter(o => o.status === 'Pendiente').length;
  // Show the higher of: new orders count or pending orders count
  const ordersBadge = newOrderCount > 0 ? newOrderCount : pendingOrdersCount > 0 ? pendingOrdersCount : undefined;
  const ordersBadgeColor = newOrderCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-amber-500';
  // Demo requests badge
  const [demoRequestsBadge, setDemoRequestsBadge] = useState(0);
  useEffect(() => {
    const updateBadge = async () => {
      try {
        const count = await getNewRequestsCount();
        setDemoRequestsBadge(count);
      } catch (e) {
        console.warn('Error getting demo requests count:', e);
      }
    };
    updateBadge();
    window.addEventListener('cricket-demo-request', () => updateBadge());
    // Subscribe to Supabase real-time updates
    let subscription: any = null;
    try {
      if (isSupabaseConfigured()) {
        const {
          supabaseClient: sc
        } = require('../lib/supabase');
        subscription = sc.subscribeToDemoRequests(() => updateBadge());
      }
    } catch (e) {}
    const interval = setInterval(updateBadge, 30000);
    return () => {
      window.removeEventListener('cricket-demo-request', () => updateBadge());
      subscription?.unsubscribe?.();
      clearInterval(interval);
    };
  }, []);
  // Reset demo badge when visiting the page
  useEffect(() => {
    if (location.pathname === '/admin/demo-requests') {
      setDemoRequestsBadge(getNewRequestsCount());
    }
  }, [location.pathname]);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const deliveryOnlyMenuGroups = [{
    title: 'Entregas',
    items: [{
      icon: Truck,
      label: 'Mis Entregas',
      path: '/admin/delivery'
    }]
  }];
  const menuGroups = [{
    title: 'Operaciones',
    items: [{
      icon: LayoutDashboard,
      label: 'Órdenes',
      path: '/admin/orders',
      badge: ordersBadge,
      badgeColor: ordersBadgeColor,
      permissionKey: 'orders'
    }, {
      icon: Tag,
      label: 'Etiquetas',
      path: '/admin/labels',
      permissionKey: 'labels'
    }, {
      icon: Truck,
      label: 'Mis Entregas',
      path: '/admin/delivery',
      permissionKey: 'delivery'
    }, {
      icon: MessageCircle,
      label: 'Chat Soporte',
      path: '/admin/chat',
      badge: unreadTotal > 0 ? unreadTotal : undefined,
      badgeColor: 'bg-red-500',
      permissionKey: 'chat'
    }, {
      icon: Package,
      label: 'Producción',
      path: '/admin/production',
      permissionKey: 'production'
    }, {
      icon: Briefcase,
      label: 'Asignaciones',
      path: '/admin/assignments',
      permissionKey: 'assignments'
    }]
  }, {
    title: 'Gestión',
    items: [{
      icon: MessageSquare,
      label: 'Reclamaciones',
      path: '/admin/claims',
      permissionKey: 'claims'
    }, {
      icon: Inbox,
      label: 'Solicitudes Demo',
      path: '/admin/demo-requests',
      badge: demoRequestsBadge > 0 ? demoRequestsBadge : undefined,
      badgeColor: 'bg-blue-500',
      permissionKey: 'users'
    }, {
      icon: Building2,
      label: 'Empresas',
      path: '/admin/companies',
      permissionKey: 'companies'
    }, {
      icon: Users,
      label: 'Empleados',
      path: '/admin/employees',
      permissionKey: 'employees'
    }, {
      icon: DollarSign,
      label: 'Subsidios',
      path: '/admin/subsidies',
      permissionKey: 'employees'
    }, {
      icon: UtensilsCrossed,
      label: 'Menú',
      path: '/admin/menu',
      permissionKey: 'menu'
    }, {
      icon: Percent,
      label: 'Promociones',
      path: '/admin/promotions',
      permissionKey: 'promotions'
    }]
  }, {
    title: 'Sistema',
    items: [{
      icon: BarChart3,
      label: 'Reportes',
      path: '/admin/reports',
      permissionKey: 'reports'
    }, {
      icon: FileText,
      label: 'Registro',
      path: '/admin/activity-log',
      permissionKey: 'activityLog'
    }, {
      icon: Settings,
      label: 'Configuración',
      path: '/admin/settings',
      permissionKey: 'settings'
    }, {
      icon: HelpCircle,
      label: 'Guía de Uso',
      path: '/admin/guide'
      // Always visible — no permissionKey
    }]
  }];
  // Filter nav items based on adminPermissions
  // Only filter if user has granular permissions (restricted admin)
  // Super admin (no granularPermissions) always sees everything
  const filterItems = (items: (typeof menuGroups)[0]['items']) => {
    const perms = user?.adminPermissions;
    const granular = (user as any)?.granularPermissions;
    // Super admin or no permissions set → show all
    if (!perms || !granular) return items;
    return items.filter(item => {
      const key = (item as any).permissionKey;
      if (!key) return true; // No permission key = always visible (e.g. Guía)
      // Strict check: only show if explicitly true
      return perms[key as keyof typeof perms] === true;
    });
  };
  const filteredMenuGroups = menuGroups.map(group => ({
    ...group,
    items: filterItems(group.items)
  })).filter(group => group.items.length > 0);
  const activeMenuGroups = isDeliveryOnly ? deliveryOnlyMenuGroups : filteredMenuGroups;
  const SidebarContent = () => <div className="flex flex-col h-full bg-white border-r border-gray-200" data-id="element-100">
      {/* Logo */}
      <div className="p-6 flex items-center gap-3" data-id="element-101">
        <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-8 w-auto object-contain" data-id="element-102" />
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-8" data-id="element-103">
        {activeMenuGroups.map(group => <div key={group.title} data-id="element-104">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2" data-id="element-105">
              {group.title}
            </h3>
            <div className="space-y-1" data-id="element-106">
              {group.items.map(item => <NavLink key={item.path} to={item.path} onClick={() => setIsOpen(false)} className={({
            isActive
          }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-107">
                  <item.icon size={18} data-id="element-108" />
                  <span className="flex-1" data-id="element-109">{item.label}</span>
                  {item.badge && <span className={`${item.badgeColor || 'bg-red-500'} text-white text-xs font-bold px-2 py-0.5 rounded-full`} data-id="element-110">
                      {item.badge}
                    </span>}
                </NavLink>)}
            </div>
          </div>)}
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200" data-id="element-111">
        <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-gray-100" data-id="element-112">
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-113">
            <User size={18} data-id="element-114" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-115">
            <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-116">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 truncate capitalize" data-id="element-117">
              {isDeliveryOnly ? 'Delivery' : user?.role === 'admin' ? 'Administrador' : 'Empleado'}
            </p>
          </div>
          <button onClick={handleLogout} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Cerrar Sesión" data-id="element-118">
            <LogOut size={18} data-id="element-119" />
          </button>
        </div>
      </div>
    </div>;
  return <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-64 fixed inset-y-0 left-0 z-30" data-id="element-120">
        <SidebarContent data-id="element-121" />
      </div>

      {/* Mobile Toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3" data-id="element-122">
        <button onClick={() => setIsOpen(true)} className="p-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-123">
          <Menu size={20} data-id="element-124" />
        </button>
        <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-6 w-auto object-contain" data-id="element-125" />
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-126">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/50 z-40 lg:hidden" data-id="element-127" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-y-0 left-0 w-64 z-50 lg:hidden" data-id="element-128">
              <div className="relative h-full" data-id="element-129">
                <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600" data-id="element-130">
                  <X size={20} data-id="element-131" />
                </button>
                <SidebarContent data-id="element-132" />
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}